import { Fragment } from "react"
import { useTranslation } from "react-i18next"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card"
import { formatNumber, formatPercent, type MetricSnapshot } from "@/shared/lib/staffing-shared"

type WhatIfTableProps = {
  scenarios: MetricSnapshot[]
  recommendedScheduledAgents: number
  serviceLevelTarget: number
  occupancyTarget: number
}

const THRESHOLD_TOLERANCE = 0.05

const getStatusTone = (value: number, target: number, prefersHighValues: boolean) => {
  if (!Number.isFinite(value)) {
    return "bg-rose-400/80"
  }

  if (prefersHighValues) {
    if (value >= target) {
      return "bg-emerald-400/80"
    }

    if (value >= target - THRESHOLD_TOLERANCE) {
      return "bg-amber-300/80"
    }

    return "bg-rose-400/80"
  }

  if (value <= target) {
    return "bg-emerald-400/80"
  }

  if (value <= target + THRESHOLD_TOLERANCE) {
    return "bg-amber-300/80"
  }

  return "bg-rose-400/80"
}

export function WhatIfTable({
  scenarios,
  recommendedScheduledAgents,
  serviceLevelTarget,
  occupancyTarget,
}: WhatIfTableProps) {
  const { t } = useTranslation()
  const formatAgents = (value: number) => formatNumber(value, Number.isInteger(value) ? 0 : 1)
  const recommendedScenario =
    scenarios.find((scenario) => scenario.isRecommended) ?? scenarios[0] ?? null
  const recommendedAvailable = recommendedScenario?.agentsAvailable ?? 0

  const variation = scenarios.reduce((acc, scenario) => {
    const delta = Math.abs(scenario.agentsAvailable - recommendedAvailable)
    return Math.max(acc, delta)
  }, 0)
  const tolerancePercent = Math.round(THRESHOLD_TOLERANCE * 100)

  return (
    <Card>
      <CardHeader className="space-y-2">
        <CardTitle className="heading-sm text-[var(--color-text-primary)]">
          {t("results.whatIf.title")}
        </CardTitle>
        <CardDescription className="body-xs text-[var(--color-text-secondary)]">
          {t("results.whatIf.subtitle", {
            variation: formatAgents(variation),
            recommended: formatAgents(recommendedScheduledAgents),
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 p-0 pb-4">
        <div className="max-w-full overflow-visible">
          <table className="min-w-[720px] w-full table-fixed border-collapse body-sm">
            <thead className="sticky top-0 bg-[var(--color-surface)]/95 backdrop-blur">
              <tr className="eyebrow text-[var(--color-text-primary)]">
                <th className="border-b border-r border-[var(--color-border)] px-4 py-3 text-center text-[var(--color-text-primary)]">
                  {t("results.whatIf.columns.agents")}
                </th>
                <th className="border-b border-r border-[var(--color-border)] px-4 py-3 text-center text-[var(--color-text-primary)]">
                  {t("results.whatIf.columns.agentsAvailable")}
                </th>
                <th className="border-b border-r border-[var(--color-border)] px-4 py-3 text-center text-[var(--color-text-primary)]">
                  {t("results.whatIf.columns.serviceLevel")}
                </th>
                <th className="border-b border-r border-[var(--color-border)] px-4 py-3 text-center text-black">
                  {t("results.whatIf.columns.occupancy")}
                </th>
                <th className="border-b border-r border-[var(--color-border)] px-4 py-3 text-center text-black">
                  {t("results.whatIf.columns.asa")}
                </th>
                <th className="border-b border-r border-[var(--color-border)] px-4 py-3 text-center text-black">
                  {t("results.whatIf.columns.immediate")}
                </th>
                <th className="border-b border-[var(--color-border)] px-4 py-3 text-center text-black">
                  {t("results.whatIf.columns.abandon")}
                </th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((scenario, index) => {
                const isRecommended =
                  typeof scenario.isRecommended === "boolean"
                    ? scenario.isRecommended
                    : scenario.scheduledAgents === recommendedScheduledAgents
                const isLastRow = index === scenarios.length - 1
                const rowAccent = isRecommended
                  ? " bg-[var(--color-brand-50)]/70 font-semibold text-[var(--color-text-primary)]"
                  : ""

                const serviceLevelBg = getStatusTone(
                  scenario.serviceLevel,
                  serviceLevelTarget,
                  true,
                )
                const occupancyBg = getStatusTone(scenario.occupancy, occupancyTarget, false)
                const rowKey = `${scenario.scheduledAgents}-${scenario.agentsAvailable}-${index}`

                return (
                  <Fragment key={rowKey}>
                    {scenario.dividerBefore && (
                      <tr
                        className="border-b border-[var(--color-border)] text-center body-sm text-[var(--color-text-secondary)]"
                        key={`divider-${rowKey}`}
                      >
                        <td className="px-4 py-2"></td>
                        <td className="px-4 py-2">---</td>
                        <td className="px-4 py-2"></td>
                        <td className="px-4 py-2">---</td>
                        <td className="px-4 py-2"></td>
                        <td className="px-4 py-2">---</td>
                        <td className="px-4 py-2"></td>
                      </tr>
                    )}
                    <tr
                      className={`table-row-gradient-hover text-[var(--color-text-primary)] transition${rowAccent}`}
                      key={`row-${rowKey}`}
                    >
                      <td
                        className={`border-b border-r border-[var(--color-border)] px-4 py-3 text-right ${
                          isLastRow ? "border-b-0" : ""
                        }`}
                      >
                        <span className="body-sm text-[var(--color-text-primary)]">
                          {formatAgents(scenario.scheduledAgents)}
                        </span>
                        {isRecommended ? (
                          <span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-[var(--color-brand-700)]">
                            {t("results.whatIf.recommended")}
                          </span>
                        ) : null}
                      </td>
                      <td
                        className={`border-b border-r border-[var(--color-border)] px-4 py-3 text-right body-sm text-[var(--color-text-primary)] ${
                          isLastRow ? "border-b-0" : ""
                        }`}
                      >
                        {formatAgents(scenario.agentsAvailable)}
                      </td>
                      <td
                        className={`border-b border-r border-[var(--color-border)] px-4 py-3 text-right body-sm text-white ${serviceLevelBg} ${
                          isLastRow ? "border-b-0" : ""
                        }`}
                      >
                        {formatPercent(scenario.serviceLevel)}
                      </td>
                      <td
                        className={`border-b border-r border-[var(--color-border)] px-4 py-3 text-right body-sm text-white ${occupancyBg} ${
                          isLastRow ? "border-b-0" : ""
                        }`}
                      >
                        {formatPercent(scenario.occupancy)}
                      </td>
                      <td
                        className={`border-b border-r border-[var(--color-border)] px-4 py-3 text-right body-sm text-[var(--color-text-primary)] ${
                          isLastRow ? "border-b-0" : ""
                        }`}
                      >
                        {Number.isFinite(scenario.asa) ? scenario.asa.toFixed(1) : "0.0"}
                      </td>
                      <td
                        className={`border-b border-r border-[var(--color-border)] px-4 py-3 text-right body-sm text-[var(--color-text-primary)] ${
                          isLastRow ? "border-b-0" : ""
                        }`}
                      >
                        {formatPercent(scenario.immediateAnswerRate)}
                      </td>
                      <td
                        className={`border-b border-[var(--color-border)] px-4 py-3 text-right body-sm text-[var(--color-text-primary)] ${
                          isLastRow ? "border-b-0" : ""
                        }`}
                      >
                        {formatPercent(scenario.abandonRate, 2)}
                      </td>
                    </tr>
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="px-4 text-left body-xs text-[var(--color-text-secondary)]">
          {t("results.whatIf.legend", { tolerance: tolerancePercent })}
        </p>
      </CardContent>
    </Card>
  )
}
